import { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json';
import {
  LayoutDashboard, Folder, Users, Columns3, ListTodo, Clock,
  CalendarDays, TrendingUp, BookOpen, Settings, Workflow, Bell, Circle,
  Building2, Handshake, Star, Contact, Globe, Inbox, Layers, ChevronDown, Pencil, Check, X,
  Trash2,
} from 'lucide-react';
import { FAVORITES_EVENT, saveFavorites } from '../../services/favorites';

/**
 * SidebarNav — renders the data-driven Drupal menu (mainMenu + admin) WITH the
 * per-item lucide icon the server sends. Two visual variants share the SAME
 * rendering so the deep-space sidebar and the horizontal header look alike (the
 * user asked for parity):
 *   - variant "sidebar" (default): vertical list, icon + label, admin block
 *     separated by a divider.
 *   - variant "header": inline row, icon + label, admin items appended inline.
 * Active highlight is handled by NavActiveSync (marks the <a href> these render,
 * dans `.orbit-shell-header nav`).
 *
 * An item carrying `children` is a GROUP (see NavigationLinkProvider — with or
 * without a `url` of its own): it renders as a disclosure — inline collapsible in
 * the sidebar, dropdown panel in the header — opened AU SURVOL à la souris, et
 * au clic (Entrée/Espace au clavier, tap au doigt) (cf. NavGroup). A group that
 * also has a `url` (« Sites » → /sites) renders its label as a link and keeps
 * the chevron as the only toggle. The server already dropped groups whose
 * children are all forbidden, so there is still no access rule here.
 *
 * Config-first : la navigation desktop est déjà portée par ce plugin (icônes,
 * survol, NavActiveSync) — aucun YAML reactive-json ne la rend ; le lien-de-groupe
 * s'ajoute donc ICI, et en YAML dans le panneau mobile (Navigation.yaml).
 *
 * SIGNETS — le menu sert aussi les favoris de l'utilisateur (cf.
 * orbit_front_app FavoriteNavigationHooks) : un enfant `source: section` est un
 * REPLI qui porte ses signets (rassemblés par type de contenu, fermé d'office
 * si `collapsed`), un enfant `source: favorite` porte l'étoile pleine qui le
 * dépingle ET le crayon qui le RENOMME EN PLACE (au survol, ou au focus au
 * clavier) — à
 * condition que `favoritesLocation` désigne le magasin `{favorites,
 * favorite_labels}` de la page. Le retrait comme le renommage écrivent,
 * enregistrent, puis émettent `favoriteschanged` : le YAML relit les menus
 * dessus. Renommer ne touche QUE la carte des libellés (url → nom) : le libellé
 * est celui qu'on a lu en épinglant, il n'a aucune raison de convenir toujours,
 * et l'URL, elle, ne bouge pas.
 *
 * reactive-json element: items / adminItems / variant / align / favoritesLocation
 * arrive as `~~.` (or literal) props, evaluated here. Defensive: non-array data
 * renders nothing.
 */
// Icons a module can declare next to its link in MODULE.navigation.yml (served
// by /api/orbit/navigation as item.icon). Curated set: lucide is imported name
// by name, so an unknown — or absent — name falls back to a neutral bullet.
// Deliberately no URL-based guessing: an icon belongs to the link's definition,
// alongside its label and its permission, not to a mapping table over here.
const ICON_BY_NAME = {
  LayoutDashboard, Folder, Users, Columns3, ListTodo, Clock,
  CalendarDays, TrendingUp, BookOpen, Settings, Workflow, Bell,
  Building2, Handshake, Star, Contact, Globe, Inbox, Layers,
};

const resolveIcon = (name) => ICON_BY_NAME[name] || Circle;

const LINK_CLASS =
  'flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm font-medium text-[color:var(--text-dark-body)] hover:text-white hover:bg-white/5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400';
const HEADER_LINK_CLASS =
  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[color:var(--text-dark-body)] hover:text-white hover:bg-white/5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400';

// Tolère les deux formes de decoupled_toolbox : l'enveloppe syncable
// ({data:{…}}, servie aux comptes qui peuvent écrire l'entité) et la donnée à
// plat. Même normalisation que menuTreeApi.js (l'éditeur de menus) et que
// DocSpace. Sans elle, ne lire que `.data.` vidait la navbar de tout compte
// sans droit d'écriture sur les menus.
const unwrap = (item) => item?.data ?? item ?? {};

// Même règle d'« item courant » que NavActiveSync, pour ouvrir d'office le
// groupe qui contient la page affichée.
const matchesPath = (pathname, href) =>
  href === '/' ? pathname === '/' : (pathname === href || pathname.startsWith(href + '/'));

function NavLink({ item, header, onRemove, rename }) {
  const d = unwrap(item);
  const url = d.link?.url || '#';
  const title = d.title || '';
  const pencilRef = useRef(null);
  const wasEditing = useRef(false);
  const favorite = d.source === 'favorite';
  // Le libellé d'un signet est celui qu'on a lu au moment de l'épingler : il ne
  // convient pas toujours (« Nouveau projet », deux fiches homonymes…). Il se
  // corrige DANS le menu, en place — pas de pop-in (cf. la règle d'édition
  // inline du projet). Le crayon ne se montre qu'au survol pour ne pas alourdir
  // la liste, mais il reste dans le DOM : au clavier, le focus le révèle.
  const editing = favorite && rename?.renaming?.url === url;

  useEffect(() => {
    // Sortie d'édition : rendre le focus au crayon, sinon il retombe sur <body>
    // et la navigation au clavier repart de zéro.
    if (wasEditing.current && !editing) pencilRef.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  if (!title) return null;

  const Icon = resolveIcon(d.icon);
  // Un signet se retire d'où il gêne : depuis le menu lui-même, par une petite
  // corbeille — l'étoile disait « épinglé », pas « retirer ». Discrète comme le
  // crayon : au survol seulement, mais toujours dans le DOM pour le clavier.
  const removable = favorite && typeof onRemove === 'function';
  const renamable = favorite && !!rename;

  if (editing) {
    return (
      <li className="flex items-center gap-0.5">
        <span className={`${header ? HEADER_LINK_CLASS : LINK_CLASS} min-w-0 flex-1 hover:bg-transparent`}>
          <Icon size={header ? 16 : 17} className="flex-none opacity-80" aria-hidden="true" />
          <input
            type="text"
            autoComplete="off"
            spellCheck={false}
            autoFocus
            value={rename.renaming.draft}
            aria-label={`Renommer le favori ${title}`}
            // Champ du DS posé sur un fond sombre : token opaque, sans habillage
            // natif ni correcteur — on retape un libellé, pas une phrase.
            className="min-w-0 flex-1 appearance-none bg-space-700 text-white rounded px-1.5 py-0.5 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400"
            onChange={(e) => rename.change(e.target.value)}
            // Échap ne doit PAS remonter : le document referme le déroulant
            // dessus, et l'annulation emporterait le menu avec elle.
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); rename.commit(); }
              else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); rename.cancel(); }
            }}
            // Filet : le déroulant peut se démonter sans que le ✓ soit cliqué.
            onBlur={() => rename.commit()}
          />
        </span>
        {/* `onMouseDown` neutralisé : sans ça le bouton perdrait son clic, le
            blur de la saisie ayant déjà démonté la ligne. */}
        <button
          type="button"
          title="Valider le nouveau nom"
          aria-label={`Valider le nouveau nom de ${title}`}
          className="flex-none p-1.5 rounded-lg cursor-pointer text-emerald-400 hover:text-emerald-300 hover:bg-white/5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => rename.commit()}
        >
          <Check size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          title="Annuler le renommage"
          aria-label={`Annuler le renommage de ${title}`}
          className="flex-none p-1.5 rounded-lg cursor-pointer text-[color:var(--text-dark-muted)] hover:text-white hover:bg-white/5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => rename.cancel()}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </li>
    );
  }

  return (
    <li className={removable || renamable ? 'group flex items-center gap-0.5' : undefined}>
      <a href={url} className={`${header ? HEADER_LINK_CLASS : LINK_CLASS}${removable || renamable ? ' min-w-0 flex-1' : ''}`}>
        <Icon size={header ? 16 : 17} className="flex-none opacity-80" aria-hidden="true" />
        <span className={header && !removable && !renamable ? 'whitespace-nowrap' : 'flex-1 truncate'}>{title}</span>
      </a>
      {renamable && (
        <button
          ref={pencilRef}
          type="button"
          title="Renommer ce favori"
          aria-label={`Renommer le favori ${title}`}
          className="flex-none p-1.5 rounded-lg cursor-pointer text-[color:var(--text-dark-muted)] hover:text-white hover:bg-white/5 transition-colors opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400"
          onClick={() => rename.start(url, title)}
        >
          <Pencil size={13} aria-hidden="true" />
        </button>
      )}
      {removable && (
        <button
          type="button"
          title="Retirer des favoris"
          aria-label={`Retirer ${title} des favoris`}
          className="flex-none p-1.5 rounded-lg cursor-pointer text-[color:var(--text-dark-muted)] hover:text-rose-500 hover:bg-white/5 transition-colors opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400"
          onClick={() => onRemove(url)}
        >
          <Trash2 size={13} aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

/**
 * Une SECTION de signets dans un déroulant : les favoris y sont rassemblés par
 * type de contenu (cf. FavoriteNavigationHooks), et chaque section est un repli
 * à part entière. Son état INITIAL vient de la donnée (`collapsed`) : les
 * sections qu'une rubrique de la barre déroule déjà arrivent fermées.
 *
 * Volontairement à l'écart de l'état `openId` des rubriques : plusieurs sections
 * peuvent être ouvertes en même temps, et refermer le déroulant les remet toutes
 * à leur défaut (le composant est démonté avec le panneau).
 */
function NavSection({ item, header, onRemove, rename }) {
  const d = unwrap(item);
  const children = Array.isArray(d.children) ? d.children : [];
  const [open, setOpen] = useState(!d.collapsed);
  const panelId = useId();
  if (!d.title || children.length === 0) return null;
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center gap-1.5 px-3 pt-2 pb-1 rounded-lg cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-dark-muted)] hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <ChevronDown
          size={13}
          aria-hidden="true"
          className={`flex-none transition-transform ${open ? '' : '-rotate-90'}`}
        />
        <span className="flex-1 text-left truncate">{d.title}</span>
        {/* Le compte dit ce que le repli cache — sinon une section fermée ne
            laisse rien deviner de ce qu'elle contient. */}
        <span className="flex-none opacity-70">{children.length}</span>
      </button>
      {open && (
        <ul id={panelId} className="flex flex-col gap-0.5">
          {children.map((child, i) => (
            <NavLink key={`s-${i}`} item={child} header={header} onRemove={onRemove} rename={rename} />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Un item de menu porteur d'enfants : bouton disclosure + sous-liste.
 * Sidebar = repli inline (indenté) ; header = panneau flottant sombre, pour que
 * la marque d'item courant de NavActiveSync (tuile iris + texte blanc) reste
 * lisible dans le panneau comme dans la barre.
 *
 * OUVERTURE AU SURVOL, mais jamais AU SEUL survol : le survol ne s'adresse qu'à
 * une souris, alors le bouton reste un disclosure à part entière — Entrée/Espace
 * au clavier, tap au doigt ouvrent le même déroulant. Sans ça, les trois écrans
 * CRM seraient inatteignables sans souris (WCAG 2.1.1). Le survol est filtré sur
 * `pointerType === 'mouse'` : un tap synthétise un `pointerenter`, qui ouvrirait
 * le menu juste avant que le clic qui suit ne le referme.
 */
function NavGroup({ item, header, groupId, openId, setOpenId, openNow, closeSoon, pathname, onRemove, rename, alignRight }) {
  const d = unwrap(item);
  const title = d.title || '';
  const children = Array.isArray(d.children) ? d.children : [];
  const panelId = `${groupId}-panel`;
  const containsCurrent = children.some((child) => {
    const url = unwrap(child).link?.url;
    return typeof url === 'string' && url.startsWith('/') && matchesPath(pathname, url);
  });
  // Dans la barre latérale, le groupe de la page courante est déroulé au repos ;
  // dans le header, un dropdown ne s'ouvre jamais tout seul.
  const open = openId === groupId || (!header && openId === null && containsCurrent);
  if (!title || children.length === 0) return null;

  const Icon = resolveIcon(d.icon);
  // Un groupe peut avoir sa propre page (déclaration AVEC `url`) : le libellé
  // devient alors un lien, le chevron seul reste le disclosure.
  const url = typeof d.link?.url === 'string' && d.link.url ? d.link.url : '';
  const toggleClass = [
    header ? HEADER_LINK_CLASS : LINK_CLASS,
    'w-full cursor-pointer',
    containsCurrent ? 'text-white font-semibold' : '',
  ].join(' ');
  // Seule une souris survole : un doigt et un stylet « entrent » aussi, mais
  // c'est leur clic/tap qui doit décider, pas ce pointerenter synthétique.
  const fromMouse = (e) => e.pointerType === 'mouse';

  return (
    <li
      className={header ? 'relative' : undefined}
      onPointerEnter={(e) => fromMouse(e) && openNow(groupId)}
      onPointerLeave={(e) => fromMouse(e) && closeSoon()}
      // Sortir du groupe au Tab referme. Le focus, lui, n'OUVRE pas : Échap
      // referme en rendant le focus au bouton, et un focus ouvrant rouvrirait
      // aussitôt ce qu'on vient de fermer. Au clavier, c'est Entrée/Espace sur
      // le bouton — le fonctionnement normal d'un disclosure — qui déroule.
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) closeSoon(0);
      }}
    >
      {url ? (
        // Groupe qui a AUSSI une page (déclaration avec `url`, ex. « Sites » →
        // /sites) : le libellé est un vrai lien, et seul le chevron déplie. Deux
        // commandes, deux cibles — un lien qui déplierait au clic ne mènerait
        // jamais à sa page au clavier.
        <div className="flex items-center gap-0.5">
          <a href={url} className={`${toggleClass} min-w-0 flex-1`}>
            <Icon size={header ? 16 : 17} className="flex-none opacity-80" aria-hidden="true" />
            <span className={header ? 'whitespace-nowrap' : 'flex-1 truncate text-left'}>{title}</span>
          </a>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={`Sous-menu ${title}`}
            className="flex-none p-1.5 rounded-lg cursor-pointer text-[color:var(--text-dark-body)] hover:text-white hover:bg-white/5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400"
            onClick={() => setOpenId(open ? '' : groupId)}
          >
            <ChevronDown
              size={15}
              aria-hidden="true"
              className={`transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      ) : (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          className={toggleClass}
          onClick={() => setOpenId(open ? '' : groupId)}
        >
          <Icon size={header ? 16 : 17} className="flex-none opacity-80" aria-hidden="true" />
          <span className={header ? 'whitespace-nowrap' : 'flex-1 truncate text-left'}>{title}</span>
          <ChevronDown
            size={15}
            aria-hidden="true"
            className={`flex-none opacity-70 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      )}
      {open && (
        <ul
          id={panelId}
          className={header
            ? `absolute ${alignRight ? 'right-0' : 'left-0'} top-full mt-2 min-w-56 z-50 flex flex-col gap-0.5 p-1.5 rounded-xl bg-space-900 border border-[color:var(--border-dark)] shadow-2xl animate-dropdown-in`
            : 'flex flex-col gap-0.5 mt-0.5 ml-4 pl-2 border-l border-[color:var(--border-dark)]'}
        >
          {children.map((child, i) => {
            // Un enfant qui a lui-même des enfants est une SECTION de signets.
            const nested = unwrap(child).children;
            return Array.isArray(nested) && nested.length > 0
              ? <NavSection key={`c-${i}`} item={child} header={header} onRemove={onRemove} rename={rename} />
              : <NavLink key={`c-${i}`} item={child} header={header} onRemove={onRemove} rename={rename} />;
          })}
        </ul>
      )}
    </li>
  );
}

function NavEntry({ item, header, index, prefix, ...group }) {
  const children = unwrap(item).children;
  if (Array.isArray(children) && children.length > 0) {
    return <NavGroup item={item} header={header} groupId={`${prefix}-${index}`} {...group} />;
  }
  return <NavLink item={item} header={header} onRemove={group.onRemove} rename={group.rename} />;
}

export function SidebarNav({ props = {}, path, datafield }) {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const ev = (v) => {
    try {
      return evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext });
    } catch {
      return undefined;
    }
  };
  const items = ev(props.items);
  const adminItems = ev(props.adminItems);
  const header = ev(props.variant) === 'header';

  const { pathname } = useLocation();
  const rootRef = useRef(null);
  const instanceId = useId();
  // `null` = aucun choix de l'utilisateur : la barre latérale ouvre alors le
  // groupe de la page courante. `''` = tout replié (choix explicite).
  const [openId, setOpenId] = useState(null);

  // Renommage EN PLACE d'un signet : `{url, draft, initial}` de la seule ligne
  // en cours d'édition (l'état est ici, pas dans la ligne, parce que le
  // déroulant doit savoir qu'une saisie est en cours pour ne pas se refermer
  // dessous). Le ref DOUBLE l'état : `closeSoon` et le clic-dehors sont appelés
  // depuis des écouteurs qui ne verraient pas la valeur du rendu courant.
  const [renaming, setRenaming] = useState(null);
  const renamingRef = useRef(null);
  // La validation, telle qu'elle est au dernier rendu : les écouteurs document
  // sont posés une fois, ils ne peuvent pas capturer la fonction du rendu courant.
  const commitRenameRef = useRef(null);

  // Ouverture au survol : immédiate à l'entrée, TEMPORISÉE à la sortie. Le
  // panneau flottant du header est détaché de son bouton (mt-2) ; sans ce délai,
  // traverser ce vide referme le menu avant qu'on l'atteigne.
  const closeTimer = useRef(null);
  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);
  // Toute écriture explicite (clic, Échap, clic dehors) annule la fermeture en
  // attente : sans ça, le minuteur ramènerait l'état par défaut juste après.
  const forceSet = useCallback((id) => { cancelClose(); setOpenId(id); }, [cancelClose]);
  // Retour à `null` (et non `''`) : au repos, la sidebar redéroule le groupe de
  // la page courante.
  // Une saisie en cours gèle la fermeture : sortir du panneau à la souris pour
  // aller ailleurs ne doit pas emporter le renommage qu'on est en train d'écrire.
  const closeSoon = useCallback((delay = 180) => {
    cancelClose();
    if (renamingRef.current) return;
    closeTimer.current = setTimeout(() => setOpenId(null), delay);
  }, [cancelClose]);
  useEffect(() => cancelClose, [cancelClose]);

  // Un changement de page repart de l'état par défaut : le dropdown du header se
  // referme après navigation, et la sidebar suit la nouvelle page.
  useEffect(() => setOpenId(null), [pathname]);

  // Clic dehors / Échap referment le déroulant ouvert (obligatoire pour le
  // panneau flottant du header, sans nuire au repli inline de la sidebar).
  useEffect(() => {
    if (!openId) return undefined;
    const onPointerDown = (e) => {
      if (!rootRef.current || rootRef.current.contains(e.target)) return;
      // Un clic dehors ferme le déroulant : il démonte la saisie sans toujours
      // lui laisser le temps d'un `blur`. On valide donc AVANT de fermer, sinon
      // le renommage en cours serait perdu sans un mot.
      commitRenameRef.current?.();
      forceSet('');
    };
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      forceSet('');
      rootRef.current?.querySelector('button[aria-expanded="true"]')?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openId, forceSet]);

  const list = Array.isArray(items) ? items : [];
  // No access test here: /api/orbit/navigation already returned exactly what
  // this user may see, so an empty list simply renders no admin block.
  const admin = Array.isArray(adminItems) ? adminItems : [];

  const ulClass = header ? 'flex items-center gap-1' : 'flex flex-col gap-0.5';
  const adminClass = header
    ? 'flex items-center gap-1'
    : 'flex flex-col gap-0.5 mt-1 pt-1 border-t border-[color:var(--border-dark)]';

  // Signets : la corbeille d'un favori écrit dans le MÊME magasin que l'étoile
  // (`{favorites, favorite_labels}`, cf. FavoriteToggle), enregistre, puis émet
  // `favoriteschanged` — l'événement sur lequel le YAML relit le menu.
  // Sans `favoritesLocation`, aucune corbeille : le menu redevient un rendu pur.
  // Prop FACULTATIVE : sans elle, pas de magasin à toucher — et surtout pas
  // d'appel au helper, qui se rabattrait sur `templateData` (un menu n'a pas de
  // donnée de template à lui).
  const favLocation = typeof props.favoritesLocation === 'string' ? props.favoritesLocation : '';
  const { formData: favStore, formDataPath: favPath } = favLocation
    ? propsDataLocationToPathAndValue({
      currentPath: path,
      datafield,
      dataLocation: favLocation,
      defaultValue: null,
      globalDataContext,
      templateContext,
    })
    : { formData: null, formDataPath: null };
  const commitEvent = ev(props.commitEvent) || FAVORITES_EVENT;
  // Écrire le magasin, enregistrer, puis n'émettre `favoriteschanged` qu'UNE
  // FOIS LA RÉPONSE REÇUE — le classement du menu est calculé par le back, une
  // relecture lancée en même temps que l'écriture courrait contre elle
  // (cf. services/favorites.js).
  const writeFavorites = useCallback((next) => {
    globalDataContext.updateData(next, favPath);
    saveFavorites(next).then((saved) => {
      if (saved) globalDataContext.updateData(saved, favPath);
      rootRef.current?.dispatchEvent(new Event(commitEvent, { bubbles: true }));
    });
  }, [favPath, commitEvent, globalDataContext]);
  // Les deux clés du magasin, toujours lues au moment d'écrire : le magasin peut
  // avoir changé entre deux rendus (une étoile posée ailleurs dans la page).
  const readFavorites = () => (Array.isArray(favStore?.favorites) ? favStore.favorites : []);
  const readLabels = () => (favStore?.favorite_labels && typeof favStore.favorite_labels === 'object'
    ? favStore.favorite_labels
    : {});
  const removeFavorite = useCallback((url) => {
    const labels = favStore?.favorite_labels && typeof favStore.favorite_labels === 'object'
      ? favStore.favorite_labels
      : {};
    const favorites = Array.isArray(favStore?.favorites) ? favStore.favorites : [];
    writeFavorites({ favorites: favorites.filter((u) => u !== url), favorite_labels: labels });
  }, [favStore, writeFavorites]);

  // Renommer = réécrire la SEULE carte des libellés (url → nom) ; la liste des
  // URLs, elle, ne bouge pas. Le nouveau nom arrive dans le menu par la
  // relecture que déclenche `favoriteschanged`.
  const setRenamingBoth = useCallback((value) => {
    renamingRef.current = value;
    setRenaming(value);
  }, []);
  const rename = favLocation ? {
    renaming,
    start: (url, title) => setRenamingBoth({ url, draft: title, initial: title }),
    change: (draft) => setRenamingBoth({ ...renamingRef.current, draft }),
    cancel: () => setRenamingBoth(null),
    commit: () => {
      const current = renamingRef.current;
      setRenamingBoth(null);
      const label = (current?.draft || '').trim();
      // Un nom vide ou inchangé n'est pas une modification : on sort sans écrire
      // (et sans faire relire le menu pour rien).
      if (!current || !label || label === current.initial) return;
      writeFavorites({
        favorites: readFavorites(),
        favorite_labels: { ...readLabels(), [current.url]: label },
      });
    },
  } : undefined;
  commitRenameRef.current = rename ? rename.commit : null;

  const entryProps = {
    header,
    openId,
    setOpenId: forceSet,
    openNow: forceSet,
    closeSoon,
    pathname,
    onRemove: favLocation ? removeFavorite : undefined,
    rename,
    // Le déroulant du bord droit s'ouvre vers l'intérieur (« Mes favoris »).
    alignRight: ev(props.align) === 'right',
  };

  return (
    // `display: contents` : le conteneur n'existe que pour le ref (clic dehors
    // ET événement de signet), la mise en page reste exactement celle d'avant
    // (ULs enfants directs du nav).
    <ActionDependant {...props} attributesHolderRef={rootRef}>
    <div ref={rootRef} className="contents">
      <ul className={ulClass}>
        {list.map((it, i) => (
          <NavEntry key={`m-${i}`} item={it} index={i} prefix={`${instanceId}-m`} {...entryProps} />
        ))}
      </ul>
      {admin.length > 0 && (
        <ul className={adminClass}>
          {admin.map((it, i) => (
            <NavEntry key={`a-${i}`} item={it} index={i} prefix={`${instanceId}-a`} {...entryProps} />
          ))}
        </ul>
      )}
    </div>
    </ActionDependant>
  );
}

export default SidebarNav;
